import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  createOrganisation,
  deleteOrganisationEndpoint as deleteOrganisationApi,
  extractOrganisationMissingExtractions,
  getOrganisationAllowanceUsage,
  getOrganisationMissingExtractions,
  getOrganisationModelSettings,
  searchOrganisation,
  updateOrganisation as updateOrganisationApi,
  updateOrganisationFeatures,
  updateOrganisationModelSettings as updateOrganisationModelSettingsApi,
  type ContractManagementMissingExtractions,
  type OrganisationCreate,
  type OrganisationInDb,
  type OrganisationMemberAccess,
  type OrganisationModelSettingsCreate,
  type OrganisationModelSettingsInDb,
  type OrganisationModelSettingsUpdate,
  type OrganisationSearchResult,
  type OrganisationUpdate,
  type OrgFeature,
  type SearchQuery,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

export const useAdminOrganizationsStore = defineStore('admin-organizations', () => {
  // 1. State
  const organizations = ref<OrganisationInDb[]>([]);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const total = ref<number>(0);
  const currentPage = ref<number>(1);
  const pageSize = ref<number>(10);
  const organizationAllowanceUsages = ref<Map<string, number>>(new Map());

  // 2. Getters
  const hasOrganizations = computed<boolean>(() => {
    return organizations.value.length > 0;
  });

  const totalPages = computed<number>(() => {
    return Math.ceil(total.value / pageSize.value);
  });

  // 3. Actions
  async function fetchOrganizations(queryOrPage?: SearchQuery | number, limit = 10): Promise<void> {
    loading.value = true;
    error.value = null;

    let query: SearchQuery;

    // Handle backward compatibility with existing code
    if (typeof queryOrPage === 'number') {
      currentPage.value = queryOrPage;
      pageSize.value = limit;
      query = {
        text: '',
        limit,
        offset: (queryOrPage - 1) * limit,
        sort_key: 'name',
        sort_direction: 'asc',
      };
    } else if (queryOrPage) {
      // Use provided query
      query = queryOrPage;

      // Update state based on query
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      pageSize.value = query.limit || 10;
      currentPage.value =
        query.offset === undefined ? 1 : Math.floor(query.offset / pageSize.value) + 1;
    } else {
      // Default query
      query = {
        text: '',
        limit: pageSize.value,
        offset: (currentPage.value - 1) * pageSize.value,
        sort_key: 'name',
        sort_direction: 'asc',
      };
    }

    try {
      const response = await searchOrganisation({ body: query });

      if (!response.data) {
        throw new Error('Failed to fetch organizations');
      }
      const result: OrganisationSearchResult = response.data;

      organizations.value = result.results;
      total.value = result.results_count;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch organizations';
      logger.error('Error fetching organizations:', error_);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetches an organization's allowance usage - admin only method
   * This method is suitable for admin users to check any organization's usage
   *
   * @param uid - The UID of the organization to fetch allowance usage for
   * @returns The organization's credit usage as a number
   */
  async function fetchOrganizationAllowanceUsage(uid: string): Promise<number | null> {
    try {
      // Make sure the uid is valid
      if (!uid) {
        logger.warn('Attempted to fetch organization allowance usage with invalid UID');
        return null;
      }

      const apiResponse = await getOrganisationAllowanceUsage({ path: { uid } });
      if (apiResponse.error) {
        throw new Error(
          extractErrorDetail(apiResponse.error, 'Failed to fetch organization allowance usage')
        );
      }
      const response = apiResponse.data;

      // If the response is NaN or negative, treat it as 0
      if (isNaN(Number(response)) || Number(response) < 0) {
        const numericValue = 0;
        organizationAllowanceUsages.value.set(uid, numericValue);
        return numericValue;
      } else {
        // Make sure we're storing a number, not a string
        const numericValue = typeof response === 'string' ? parseFloat(response) : Number(response);
        organizationAllowanceUsages.value.set(uid, numericValue);
        return numericValue;
      }
    } catch (error_: unknown) {
      logger.error('Error fetching organization allowance usage:', error_);
      organizationAllowanceUsages.value.set(uid, 0); // Default to 0 on error
      return 0;
    }
  }

  /**
   * Get the cached allowance usage for an organization
   * @param uid - The organization UID
   * @returns The organization's credit usage or null if not found
   */
  function getOrganizationAllowanceUsage(uid: string): number | null {
    return organizationAllowanceUsages.value.get(uid) ?? null;
  }

  async function createOrganization(
    organisationData: OrganisationCreate,
    modelSettings: OrganisationModelSettingsCreate,
    memberAccess: OrganisationMemberAccess
  ): Promise<OrganisationInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await createOrganisation({
        body: {
          organisation_in: organisationData,
          model_settings: modelSettings,
          member_access: memberAccess,
        },
      });

      if (!response.data) {
        throw new Error('Failed to create organization');
      }
      const result = response.data;

      // Refresh the list after creation
      await fetchOrganizations(currentPage.value, pageSize.value);
      return result;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to create organization';
      logger.error('Error creating organization:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateOrganization(
    uid: string,
    data: OrganisationUpdate,
    features: OrgFeature[]
  ): Promise<OrganisationInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await updateOrganisationApi({
        path: { uid },
        body: data,
      });

      if (!response.data) {
        throw new Error('Failed to update organization');
      }
      const result = response.data;

      const featuresResponse = await updateOrganisationFeatures({
        body: { features },
        path: { uid },
      });
      if (featuresResponse.error) {
        throw new Error(
          extractErrorDetail(featuresResponse.error, 'Failed to update organization features')
        );
      }

      // Refresh the list after update
      await fetchOrganizations(currentPage.value, pageSize.value);
      return result;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to update organization';
      logger.error('Error updating organization:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function fetchOrganizationModelSettings(
    uid: string
  ): Promise<OrganisationModelSettingsInDb | null> {
    try {
      const response = await getOrganisationModelSettings({ path: { uid } });
      // A missing settings record (404) is a valid state for older organisations.
      return response.data ?? null;
    } catch (error_) {
      logger.error('Error fetching organization model settings:', error_);
      return null;
    }
  }

  async function updateOrganizationModelSettings(
    uid: string,
    settings: OrganisationModelSettingsUpdate
  ): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await updateOrganisationModelSettingsApi({
        path: { uid },
        body: settings,
      });

      if (response.error) {
        throw new Error(
          extractErrorDetail(response.error, 'Failed to update organization model settings')
        );
      }
      return true;
    } catch (error_) {
      error.value =
        error_ instanceof Error ? error_.message : 'Failed to update organization model settings';
      logger.error('Error updating organization model settings:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Calendar and view-column extractions the organisation is missing — they
   * don't run while contract management is off and aren't caught up when it is
   * switched back on. Doesn't touch `loading`/`error`: it's a side panel of the
   * edit modal, not a page-level load.
   */
  async function fetchMissingExtractions(
    uid: string
  ): Promise<ContractManagementMissingExtractions | null> {
    const response = await getOrganisationMissingExtractions({ path: { uid } });
    if (response.error) {
      logger.error('Error fetching missing extractions:', response.error);
      return null;
    }
    return response.data;
  }

  /**
   * Starts the back-fill in the background and returns the counts as of the
   * request. Throws with the backend's detail (e.g. the 400 while contract
   * management is off) so the caller can surface it.
   */
  async function runMissingExtractions(uid: string): Promise<ContractManagementMissingExtractions> {
    const response = await extractOrganisationMissingExtractions({ path: { uid } });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to start missing extractions'));
    }
    return response.data;
  }

  async function deleteOrganization(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      await deleteOrganisationApi({ path: { uid } });

      // Refresh the list after deletion
      await fetchOrganizations(currentPage.value, pageSize.value);
      return true;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to delete organization';
      logger.error('Error deleting organization:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    organizations,
    loading,
    error,
    total,
    currentPage,
    pageSize,
    organizationAllowanceUsages,

    // Getters
    hasOrganizations,
    totalPages,

    // Actions
    fetchOrganizations,
    fetchOrganizationAllowanceUsage,
    fetchMissingExtractions,
    runMissingExtractions,
    getOrganizationAllowanceUsage,
    createOrganization,
    updateOrganization,
    fetchOrganizationModelSettings,
    updateOrganizationModelSettings,
    deleteOrganization,
  };
});
